import { useEffect, useMemo, useState } from 'react';
import createDOMPurify, { type Config } from 'dompurify';
import './OrcRichText.css';
import { useLocalization, useTranslation } from '../i18n/provider';
import { pf2eApi } from '../api/client';
import { usePF2eTranslation } from '../hooks/usePF2eTranslation';
import { useOrcKnowledgeModal, type OrcKnowledgeLinkTarget } from './OrcKnowledgeModalContext';
import {
  escapeHtml,
  inlineActionSlug,
  parseRichTextTokens,
  renderRichTextMarkup,
  type OrcRichTextRenderContext,
} from '../utils/orcRichText';

export type RichTextProps = {
  content: string | null | undefined;
  html?: boolean;
  markdown?: boolean;
  /** Parse trusted PF2e/Foundry rules markup such as @ORC and @Damage. */
  rulesMarkup?: boolean;
  inline?: boolean;
  className?: string;
  context?: OrcRichTextRenderContext;
  onOrcLinkClick?: (target: OrcKnowledgeLinkTarget) => void;
};

const ORC_LABEL_CACHE = new Map<string, string>();
const CYRILLIC_RE = /[А-Яа-яЁё]/;
const DOMPurify = createDOMPurify(window);
const TRUSTED_EXTERNAL_HOSTS = ['2e.aonprd.com', 'paizo.com'];

function isTrustedExternalHost(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return TRUSTED_EXTERNAL_HOSTS.some(
    trusted => normalized === trusted || normalized.endsWith(`.${trusted}`),
  );
}

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName !== 'A') return;
  const anchor = node as HTMLAnchorElement;
  const href = anchor.getAttribute('href')?.trim() || '';
  anchor.removeAttribute('target');
  anchor.removeAttribute('rel');

  if (!href || href.startsWith('#') || (href.startsWith('/') && !href.startsWith('//'))) {
    return;
  }

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin === window.location.origin) return;
    if (url.protocol === 'https:' && isTrustedExternalHost(url.hostname)) {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
      return;
    }
  } catch {
    // Invalid and unsupported URLs become inert text links below.
  }
  anchor.removeAttribute('href');
});

const SANITIZE_CONFIG: Config = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'hr',
    'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'span', 'div', 'a',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
    'blockquote', 'code', 'pre',
  ],
  ALLOWED_ATTR: [
    'class',
    'href',
    'title',
    'target',
    'rel',
    'colspan',
    'rowspan',
    'scope',
    'aria-label',
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|\/|#)/i,
  FORBID_ATTR: ['style', 'onerror', 'onclick', 'onload'],
  // Rules renderer adds its own data attributes only after sanitization.
  // Source HTML must never be able to forge an interactive ORC target.
  ALLOW_DATA_ATTR: false,
};

const USER_CONTENT_SANITIZE_CONFIG: Config = {
  ...SANITIZE_CONFIG,
  // User-authored content must not opt into application CSS or ARIA behavior.
  ALLOWED_ATTR: ['href', 'title', 'colspan', 'rowspan', 'scope'],
  ALLOW_ARIA_ATTR: false,
};

function sanitizeHtml(content: string, rulesMarkup = true): string {
  return DOMPurify.sanitize(
    content,
    rulesMarkup ? SANITIZE_CONFIG : USER_CONTENT_SANITIZE_CONFIG,
  );
}

function materializeUnsupportedFoundryMarkup(content: string): string {
  return content
    .replace(/@(UUID|Embed)\[[^\]]*]\{([^{}]*)\}/g, '$2')
    .replace(/@(UUID|Embed)\[[^\]]*]/g, '');
}

function chooseOrcLabel(language: string, type: string, id: string, label?: string): string {
  const locale = language.split('-', 1)[0].toLowerCase();
  const inlineLabel = label?.trim();
  const cachedLabel = ORC_LABEL_CACHE.get(`${language}|${type}:${id}`);
  if (!inlineLabel) return cachedLabel || `${type}:${id}`;
  if (locale === 'ru' && CYRILLIC_RE.test(inlineLabel)) return inlineLabel;
  if (locale === 'en') return inlineLabel;
  return cachedLabel || inlineLabel;
}

function transformMarkdown(
  content: string,
  resolveLabel: (type: string, id: string, label?: string) => string,
  inline: boolean,
  context: OrcRichTextRenderContext | undefined,
  translateRuleLabel: (
    key: string,
    fallback: string,
    params?: Record<string, string | number>,
  ) => string,
  translateRuleValue: (
    kind: 'skill' | 'save' | 'damageType' | 'templateType' | 'checkType',
    value: string,
  ) => string,
  rulesMarkup: boolean,
): string {
  let rendered = escapeHtml(content)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/\*([^*\n]+)\*/g, '<em>$1</em>')
    .replace(/_([^_\n]+)_/g, '<em>$1</em>');

  if (!inline) {
    rendered = rendered.replace(/\n/g, '<br />');
  }

  const sanitized = sanitizeHtml(rendered, rulesMarkup);
  if (!rulesMarkup) return sanitized;

  return renderRichTextMarkup(sanitized, {
    html: true,
    inline,
    context,
    resolveOrcLabel: resolveLabel,
    translateRuleLabel,
    translateRuleValue,
  });
}

export default function RichText({
  content,
  html = false,
  markdown = false,
  rulesMarkup = true,
  inline = false,
  className,
  context,
  onOrcLinkClick,
}: RichTextProps) {
  const t = useTranslation();
  const { language } = useLocalization();
  const { tSkill, tSave, tTrait } = usePF2eTranslation();
  const { openOrcKnowledgeTarget } = useOrcKnowledgeModal();
  const [, setLabelVersion] = useState(0);

  const orcTargets = useMemo(() => {
    if (!content || !rulesMarkup) return [];
    return Array.from(new Set(
      parseRichTextTokens(content)
        .flatMap((token) => {
          if (token.kind === 'orc') return [`${token.type}:${token.id}`];
          if (token.kind === 'inlineRoll') {
            const actionSlug = inlineActionSlug(token.command);
            return actionSlug ? [`action:${actionSlug}`] : [];
          }
          return [];
        })
    ));
  }, [content, rulesMarkup]);

  useEffect(() => {
    if (!orcTargets.length) return;
    const missing = orcTargets.filter(target => !ORC_LABEL_CACHE.has(`${language}|${target}`));
    if (!missing.length) return;

    let cancelled = false;
    pf2eApi.resolveOrcLabels(missing, language)
      .then((response) => {
        for (const [target, label] of Object.entries(response.data.labels || {})) {
          if (label) ORC_LABEL_CACHE.set(`${language}|${target}`, label);
        }
        if (!cancelled) setLabelVersion(version => version + 1);
      })
      .catch(() => {
        // Keep inline labels from the rich-text source when the DB resolver cannot answer.
      });
    return () => { cancelled = true; };
  }, [language, orcTargets]);

  const resolveLabel = useMemo(() => (
    (type: string, id: string, label?: string): string => {
      return chooseOrcLabel(language, type, id, label);
    }
  ), [language]);

  const renderedContent = (() => {
    if (!content) return '';
    const translateRuleLabel = (
      key: string,
      fallback: string,
      params?: Record<string, string | number>,
    ) => t(`character_creation.orcRule.${key}`, params || {}, fallback);
    const translateRuleValue = (
      kind: 'skill' | 'save' | 'damageType' | 'templateType' | 'checkType',
      value: string,
    ): string => {
      if (kind === 'skill') return tSkill(value);
      if (kind === 'save') return tSave(value);
      if (kind === 'damageType') {
        const trait = tTrait(value);
        if (language === 'en' || trait !== value) return trait;
        return t(`companion.damageType.${value.toLowerCase()}`, {}, '');
      }
      return t(`character_creation.orcRule.${kind}.${value.toLowerCase()}`, {}, '');
    };
    const sourceContent = rulesMarkup
      ? materializeUnsupportedFoundryMarkup(content)
      : content;
    if (markdown) {
      return transformMarkdown(
        sourceContent,
        resolveLabel,
        inline,
        context,
        translateRuleLabel,
        translateRuleValue,
        rulesMarkup,
      );
    }
    if (!rulesMarkup) {
      const literal = html ? sourceContent : escapeHtml(sourceContent).replace(/\n/g, inline ? '\n' : '<br />');
      return sanitizeHtml(literal, false);
    }
    return renderRichTextMarkup(html ? sanitizeHtml(sourceContent) : sourceContent, {
      html,
      inline,
      context,
      resolveOrcLabel: resolveLabel,
      translateRuleLabel,
      translateRuleValue,
    });
  })();

  if (!content) return null;

  const Wrapper = inline ? 'span' : 'div';
  const wrapperClassName = [
    !inline ? 'orc-rich-text' : 'orc-rich-text-inline',
    className || '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <Wrapper
        className={wrapperClassName}
        onClick={(event) => {
          const target = event.target as HTMLElement | null;
          const button = target?.closest('[data-orc-link="true"]') as HTMLElement | null;
          if (!button) return;

          event.preventDefault();
          const payload = {
            type: button.dataset.orcType || '',
            id: button.dataset.orcId || '',
            label: button.dataset.orcLabel || `${button.dataset.orcType || ''}:${button.dataset.orcId || ''}`,
          };
          if (onOrcLinkClick) {
            onOrcLinkClick(payload);
          } else {
            openOrcKnowledgeTarget(payload);
          }
        }}
        dangerouslySetInnerHTML={{ __html: renderedContent }}
      />
    </>
  );
}
